import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, CreditCard, CheckCircle, AlertCircle, 
  Shield, Clock, Copy, Check, ChevronDown, Info, 
  Banknote, Building2, Smartphone, QrCode, Download, Upload
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';

const BANK_DETAILS = {
  'CIH Bank': {
    bank: 'CIH Bank',
    accountNumber: '011780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '011780000012345678901234',
    swift: 'CIHMMAMC',
    address: 'Casablanca, Maroc'
  },
  'Attijariwafa Bank': {
    bank: 'Attijariwafa Bank',
    accountNumber: '007780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '007780000012345678901234',
    swift: 'BCMAMAMC',
    address: 'Casablanca, Maroc'
  },
  'BMCE Bank of Africa': {
    bank: 'BMCE Bank of Africa',
    accountNumber: '030780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '030780000012345678901234',
    swift: 'BMCAMAMC',
    address: 'Casablanca, Maroc'
  },
  'Banque Populaire': {
    bank: 'Banque Populaire',
    accountNumber: '017780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '017780000012345678901234',
    swift: 'BPAMMAMC',
    address: 'Casablanca, Maroc'
  },
  'Crédit du Maroc': {
    bank: 'Crédit du Maroc',
    accountNumber: '018780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '018780000012345678901234',
    swift: 'CDMAMAMC',
    address: 'Casablanca, Maroc'
  },
  'Société Générale': {
    bank: 'Société Générale',
    accountNumber: '019780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '019780000012345678901234',
    swift: 'SOGEMAMC',
    address: 'Casablanca, Maroc'
  },
  'Barid Bank': {
    bank: 'Barid Bank',
    accountNumber: '020780000012345678901234',
    accountHolder: 'AVKAMAS Trading',
    rib: '020780000012345678901234',
    swift: 'BRMDMAMC',
    address: 'Casablanca, Maroc'
  },
  'Orange Money': {
    bank: 'Orange Money',
    accountNumber: '+212 600 000 000',
    accountHolder: 'AVKAMAS Trading',
    rib: 'N/A',
    swift: 'N/A',
    address: 'Mobile Payment'
  },
  'Inwi Money': {
    bank: 'Inwi Money',
    accountNumber: '+212 700 000 000',
    accountHolder: 'AVKAMAS Trading',
    rib: 'N/A',
    swift: 'N/A',
    address: 'Mobile Payment'
  }
};

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  const [orderData, setOrderData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Bank Transfer');
  const [selectedBank, setSelectedBank] = useState('CIH Bank');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [cashFormData, setCashFormData] = useState({
    name: '',
    phone: '',
    preferredTime: '',
    location: ''
  });

  useEffect(() => {
    // Get order data from location state or localStorage
    const pendingOrder = location.state?.orderData || 
                        JSON.parse(localStorage.getItem('pendingOrder') || 'null');
    
    if (pendingOrder) {
      setOrderData(pendingOrder);
      if (!location.state?.orderData) {
        localStorage.setItem('pendingOrder', JSON.stringify(pendingOrder));
      }
    } else {
      navigate('/');
    }

    // Countdown timer
    const interval = setInterval(() => {
      setTimeLeft(t => Math.max(0, t - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [location.state, navigate]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Validation based on payment method
    if (selectedPaymentMethod === 'Bank Transfer' && !screenshotFile) {
      setError('Veuillez uploader un screenshot de votre virement');
      return;
    }

    if (selectedPaymentMethod === 'Cash') {
      if (!cashFormData.name.trim() || !cashFormData.phone.trim()) {
        setError('Veuillez remplir vos informations de contact');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Update order status
      const updatedOrder = {
        ...orderData,
        status: 'paid',
        paymentMethod: selectedPaymentMethod,
        paymentDetails: selectedPaymentMethod === 'Bank Transfer' ? BANK_DETAILS[selectedBank] : null,
        paymentDate: new Date().toISOString(),
        userId: user?.id || 'guest',
        screenshot: screenshotPreview,
        cashDetails: selectedPaymentMethod === 'Cash' ? cashFormData : null
      };

      // Send email for Cash payment
      if (selectedPaymentMethod === 'Cash') {
        const emailData = {
          to: 'commande@achatventekamas.com',
          subject: `Nouvelle commande Cash - ${cashFormData.name}`,
          body: `
            Nom: ${cashFormData.name}
            Téléphone: ${cashFormData.phone}
            Heure préférée: ${cashFormData.preferredTime || 'Non spécifié'}
            Lieu préféré: ${cashFormData.location || 'Non spécifié'}
            
            Détails de la commande:
            Type: ${orderData.type}
            Serveur: ${orderData.server}
            Quantité: ${orderData.quantity}M
            Prix total: ${orderData.totalPrice} DH
          `
        };

        // Send email via backend API
        try {
          await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/send_cash_order.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(emailData),
          });
        } catch (emailError) {
          console.error('Email sending failed:', emailError);
          // Continue even if email fails
        }
      }

      // Store in localStorage (in real app, send to backend)
      localStorage.setItem('completedOrder', JSON.stringify(updatedOrder));
      localStorage.removeItem('pendingOrder');

      setSuccess(true);
      
      // Redirect to customer service chat after success
      setTimeout(() => {
        navigate('/customer-service', { state: { orderData: updatedOrder } });
      }, 2000);

    } catch (err) {
      setError('Erreur lors du traitement du paiement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!orderData) return null;

  if (success) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <CheckCircle className="w-24 h-24 text-green-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Paiement réussi !</h2>
          <p className="text-gray-400">Redirection vers le chat P2P...</p>
        </motion.div>
      </div>
    );
  }

  const bankDetails = selectedPaymentMethod === 'Bank Transfer' ? BANK_DETAILS[selectedBank] : null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-3">
          <ArrowLeft size={16} />
          Retour
          <Shield className="w-4 h-4 text-green-400 ml-2" />
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-5 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full mb-3">
              <CreditCard size={16} className="text-yellow-400" />
              <span className="text-yellow-400 text-xs sm:text-sm font-medium">Paiement sécurisé</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1.5">Complétez votre paiement</h1>
            <p className="text-sm sm:text-base text-gray-400">Finalisez votre commande de Kamas</p>
          </div>

          {/* Timer */}
          <div className="bg-gray-900/80 border border-gray-800 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-400">Temps restant</span>
            </div>
            <span className="text-2xl font-bold text-yellow-400">{formatTime(timeLeft)}</span>
          </div>

          {error && (
            <motion.div 
              className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={18} />
              {error}
            </motion.div>
          )}

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Order Summary */}
            <motion.div 
              className="bg-gray-900/80 border border-gray-800 rounded-2xl p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Info className="text-yellow-400" />
                Récapitulatif de la commande
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-400">
                  <span>Type</span>
                  <span className="text-white font-medium">
                    {orderData.type === 'buy' ? 'Achat' : 'Vente'} de Kamas
                  </span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Serveur</span>
                  <span className="text-white font-medium">{orderData.server}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Quantité</span>
                  <span className="text-white font-medium">{orderData.quantity}M</span>
                </div>
                {orderData.character && (
                  <div className="flex justify-between text-gray-400">
                    <span>Personnage</span>
                    <span className="text-white font-medium">{orderData.character}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-400">
                  <span>Prix par million</span>
                  <span className="text-white font-medium">{(orderData.totalPrice / orderData.quantity).toFixed(2)} DH</span>
                </div>
                <div className="h-px bg-gray-700" />
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Total</span>
                  <span className="text-3xl font-bold text-yellow-400">{orderData.totalPrice.toFixed(2)} DH</span>
                </div>
              </div>

              <div className="bg-black/40 rounded-xl p-4 space-y-2 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-green-400" />
                  <span>Livraison garantie sous 30 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-green-400" />
                  <span>Support client 24/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-green-400" />
                  <span>Paiement 100% sécurisé</span>
                </div>
              </div>
            </motion.div>

            {/* Payment Form */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Payment Method Selection */}
              <div className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <CreditCard className="text-yellow-400" />
                  Méthode de paiement
                </h3>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('Bank Transfer')}
                    className={`w-full p-4 rounded-xl border-2 transition-all ${
                      selectedPaymentMethod === 'Bank Transfer'
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`w-6 h-6 ${selectedPaymentMethod === 'Bank Transfer' ? 'text-yellow-400' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="font-medium">Virement bancaire</p>
                        <p className="text-sm text-gray-400">Transfert direct sécurisé</p>
                      </div>
                      {selectedPaymentMethod === 'Bank Transfer' && (
                        <Check className="w-5 h-5 text-yellow-400 ml-auto" />
                      )}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('Cash')}
                    className={`w-full p-4 rounded-xl border-2 transition-all ${
                      selectedPaymentMethod === 'Cash'
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Banknote className={`w-6 h-6 ${selectedPaymentMethod === 'Cash' ? 'text-yellow-400' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="font-medium">Espèces</p>
                        <p className="text-sm text-gray-400">Paiement en main propre</p>
                      </div>
                      {selectedPaymentMethod === 'Cash' && (
                        <Check className="w-5 h-5 text-yellow-400 ml-auto" />
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {/* Bank Details (for Bank Transfer) */}
              {selectedPaymentMethod === 'Bank Transfer' && (
                <motion.div
                  className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Building2 className="text-yellow-400" />
                    Coordonnées bancaires
                  </h3>

                  {/* Bank Selection */}
                  <div className="relative mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Sélectionnez votre banque
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowBankDropdown(!showBankDropdown)}
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg py-3 px-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 size={18} className="text-yellow-400" />
                        <span>{selectedBank}</span>
                      </div>
                      <ChevronDown size={18} className="text-gray-500" />
                    </button>

                    <AnimatePresence>
                      {showBankDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute z-10 w-full mt-2 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden"
                        >
                          {Object.keys(BANK_DETAILS).map((bank) => (
                            <button
                              key={bank}
                              type="button"
                              onClick={() => {
                                setSelectedBank(bank);
                                setShowBankDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors border-b border-gray-700 last:border-0"
                            >
                              {bank}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {bankDetails && (
                    <div className="space-y-4">
                      <div className="bg-black/40 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Banque</span>
                          <span className="font-medium">{bankDetails.bank}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Titulaire</span>
                          <span className="font-medium">{bankDetails.accountHolder}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Numéro de compte</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{bankDetails.accountNumber}</span>
                            <button
                              type="button"
                              onClick={() => handleCopy(bankDetails.accountNumber)}
                              className="p-1 hover:bg-gray-700 rounded"
                            >
                              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-gray-400" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">RIB</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{bankDetails.rib}</span>
                            <button
                              type="button"
                              onClick={() => handleCopy(bankDetails.rib)}
                              className="p-1 hover:bg-gray-700 rounded"
                            >
                              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-gray-400" />}
                            </button>
                          </div>
                        </div>
                        {bankDetails.swift !== 'N/A' && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">SWIFT/BIC</span>
                            <span className="font-mono text-sm">{bankDetails.swift}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                        <p className="text-sm text-yellow-400">
                          <strong>Important:</strong> Effectuez le virement vers ce compte et uploadez un screenshot de preuve.
                        </p>
                      </div>

                      {/* Screenshot Upload */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Preuve de virement (screenshot)
                        </label>
                        <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center hover:border-yellow-500/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleScreenshotChange}
                            className="hidden"
                            id="screenshot-upload"
                          />
                          <label
                            htmlFor="screenshot-upload"
                            className="cursor-pointer"
                          >
                            {screenshotPreview ? (
                              <div className="space-y-3">
                                <img
                                  src={screenshotPreview}
                                  alt="Screenshot preview"
                                  className="max-h-48 mx-auto rounded-lg"
                                />
                                <p className="text-sm text-gray-400">Cliquez pour changer l'image</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <Upload className="w-12 h-12 text-gray-500 mx-auto" />
                                <p className="text-gray-400">Cliquez pour uploader votre screenshot</p>
                                <p className="text-xs text-gray-500">PNG, JPG jusqu'à 5MB</p>
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Cash Payment Info */}
              {selectedPaymentMethod === 'Cash' && (
                <motion.div
                  className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Banknote className="text-yellow-400" />
                    Paiement en espèces
                  </h3>

                  <div className="space-y-4">
                    <div className="bg-black/40 rounded-xl p-4">
                      <p className="text-sm text-gray-400 mb-4">
                        Pour le paiement en espèces, vous devrez rencontrer un de nos agents à un point de rendez-vous sécurisé.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Check size={14} className="text-green-400" />
                          <span>Point de rencontre sécurisé</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Check size={14} className="text-green-400" />
                          <span>Remise immédiate des Kamas</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Check size={14} className="text-green-400" />
                          <span>Reçu de paiement fourni</span>
                        </div>
                      </div>
                    </div>

                    {/* Cash Payment Form */}
                    <div className="bg-gray-950 rounded-xl p-4 space-y-4">
                      <h4 className="font-medium text-white">Vos informations de contact</h4>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Nom complet *</label>
                        <input
                          type="text"
                          value={cashFormData.name}
                          onChange={(e) => setCashFormData({...cashFormData, name: e.target.value})}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 focus:outline-none"
                          placeholder="Votre nom"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Téléphone *</label>
                        <input
                          type="tel"
                          value={cashFormData.phone}
                          onChange={(e) => setCashFormData({...cashFormData, phone: e.target.value})}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 focus:outline-none"
                          placeholder="+212 6XX XXX XXX"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Heure préférée</label>
                        <input
                          type="text"
                          value={cashFormData.preferredTime}
                          onChange={(e) => setCashFormData({...cashFormData, preferredTime: e.target.value})}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 focus:outline-none"
                          placeholder="Ex: 14h-16h"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Lieu préféré</label>
                        <input
                          type="text"
                          value={cashFormData.location}
                          onChange={(e) => setCashFormData({...cashFormData, location: e.target.value})}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 focus:outline-none"
                          placeholder="Ex: Café X, Centre ville"
                        />
                      </div>
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                      <p className="text-sm text-yellow-400">
                        <strong>Email:</strong> commande@achatventekamas.com
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Ces informations seront envoyées par email pour organiser le rendez-vous.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Confirm Payment Button */}
              <motion.button
                onClick={handlePaymentSubmit}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl font-bold text-lg hover:from-yellow-400 hover:to-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Confirmer le paiement de {orderData.totalPrice.toFixed(2)} DH
                  </>
                )}
              </motion.button>

              {/* Security Notice */}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <Shield size={16} className="text-green-400" />
                <span>Paiement 100% sécurisé et crypté</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
